import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/layout/PageHeader';
import { useCurrentUser } from '../auth';
import { listAccounts } from '../accounts/api';
import { ApiError } from '../../lib/api';
import type { Broadcast } from '../../types/broadcast';
import { listDeletedBroadcasts, restoreBroadcast } from './api';
import { BroadcastTable } from './BroadcastTable';

// Stage 6A v6 — admin recovery view at /broadcasts/deleted. Lists soft-deleted
// broadcasts (deletedAt / deletedBy) and offers a per-row Restore, which clears
// the soft-delete and returns the broadcast to the main list.

export function DeletedBroadcastsPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const deleted = useQuery({
    queryKey: ['broadcasts', 'deleted'],
    queryFn: listDeletedBroadcasts,
    enabled: me.role === 'admin',
  });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const brandName = (id: string) => accounts.data?.find((a) => a.id === id)?.name ?? '—';

  const restore = useMutation({
    mutationFn: (b: Broadcast) => restoreBroadcast(b.id),
    onMutate: (b) => {
      setRestoringId(b.id);
      setError(null);
    },
    // Prefix-invalidate ['broadcasts'] so BOTH this deleted list and the main
    // list refetch — the restored broadcast leaves here and reappears there.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcasts'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Restore failed.'),
    onSettled: () => setRestoringId(null),
  });

  if (me.role !== 'admin') {
    return <p className="text-sm text-slate-600">Only admins can view deleted broadcasts.</p>;
  }

  return (
    <div>
      <PageHeader
        title="Deleted broadcasts"
        description="Soft-deleted broadcasts (sent broadcasts are never hard-deleted). Restoring returns one to the main list; recipient and send data were preserved throughout."
        backHref="/broadcasts"
      />

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {deleted.isLoading ? (
        <CenteredSpinner />
      ) : deleted.error ? (
        <p className="text-sm text-red-600">Couldn't load deleted broadcasts.</p>
      ) : deleted.data && deleted.data.length > 0 ? (
        <BroadcastTable
          mode="deleted"
          broadcasts={deleted.data}
          brandName={brandName}
          onRestore={(b) => restore.mutate(b)}
          restoringId={restoringId}
        />
      ) : (
        <p className="text-sm text-slate-600">No deleted broadcasts.</p>
      )}
    </div>
  );
}
